import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {Loader2, Search, X} from 'lucide-react';
import clsx from 'clsx';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {AdminService} from '@/modules/admin/services/adminService';
import {useOhdOrganizations} from '@/modules/ohd/hooks/useOhdOrganizations';
import type {OhdOrganizationListEntry} from '@/modules/ohd/types';
import type {OrganizationSearchResult} from '@/shared/types/api';

interface Props {
    open: boolean;
    onClose: () => void;
    allMldOrgs: OrganizationSearchResult[];
}

type Mode = 'blank' | 'ohd' | 'elis';

interface ElisAccountMatch {
    elisAccountId: number;
    name: string;
    mldOrg: {id: number; name: string; subdomain: string} | null;
}

const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

function readOhdId(features: Record<string, unknown> | null | undefined): number | undefined {
    const raw = features?.ohd_organization_id;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string' && /^\d+$/.test(raw)) return Number(raw);
    return undefined;
}

const AddOrganizationModal: React.FC<Props> = ({open, onClose, allMldOrgs}) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [mode, setMode] = useState<Mode>('blank');
    const [name, setName] = useState('');
    const [subdomain, setSubdomain] = useState('');
    const [selectedOhd, setSelectedOhd] = useState<OhdOrganizationListEntry | null>(null);
    const [ohdSearch, setOhdSearch] = useState('');
    const [elisSearch, setElisSearch] = useState('');
    const [debouncedElisSearch, setDebouncedElisSearch] = useState('');
    const [elisResults, setElisResults] = useState<ElisAccountMatch[]>([]);
    const [elisSearching, setElisSearching] = useState(false);
    const [selectedElis, setSelectedElis] = useState<ElisAccountMatch | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedElisSearch(elisSearch.trim()), 300);
        return () => clearTimeout(t);
    }, [elisSearch]);

    useEffect(() => {
        if (mode !== 'elis' || debouncedElisSearch.length < 2) {
            setElisResults([]);
            return;
        }
        let cancelled = false;
        setElisSearching(true);
        AdminService.searchElisAccounts(debouncedElisSearch)
            .then((r) => {
                if (!cancelled) setElisResults(r.results);
            })
            .catch(() => {
                if (!cancelled) setElisResults([]);
            })
            .finally(() => {
                if (!cancelled) setElisSearching(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedElisSearch, mode]);

    const ohdOrgsQuery = useOhdOrganizations(open && mode === 'ohd');
    const ohdOrgs = ohdOrgsQuery.data ?? [];

    const ohdToMld = useMemo(() => {
        const map = new Map<number, OrganizationSearchResult>();
        for (const org of allMldOrgs) {
            const id = readOhdId(org.features);
            if (typeof id === 'number') map.set(id, org);
        }
        return map;
    }, [allMldOrgs]);

    const filteredOhd = useMemo(() => {
        const q = ohdSearch.trim().toLowerCase();
        if (!q) return ohdOrgs;
        return ohdOrgs.filter(
            (o) =>
                o.organization_name.toLowerCase().includes(q) ||
                String(o.organization_id).includes(q) ||
                o.campaigns.some((c) => c.name.toLowerCase().includes(q))
        );
    }, [ohdOrgs, ohdSearch]);

    const existingSubdomain = useMemo(() => {
        const sub = subdomain.trim().toLowerCase();
        if (!sub) return false;
        return allMldOrgs.some((o) => o.subdomain === sub);
    }, [subdomain, allMldOrgs]);

    useEffect(() => {
        if (!open) {
            setMode('blank');
            setName('');
            setSubdomain('');
            setSelectedOhd(null);
            setOhdSearch('');
            setElisSearch('');
            setDebouncedElisSearch('');
            setElisResults([]);
            setElisSearching(false);
            setSelectedElis(null);
            setSaving(false);
            setError(null);
        }
    }, [open]);

    function selectOhdOrg(ohd: OhdOrganizationListEntry) {
        setSelectedOhd(ohd);
        setName(ohd.organization_name);
        setOhdSearch('');
    }

    const subdomainValid =
        subdomain.trim().length > 0 &&
        SUBDOMAIN_PATTERN.test(subdomain.trim().toLowerCase()) &&
        !existingSubdomain;

    const canSubmit = !saving && subdomainValid && (() => {
        if (mode === 'elis') {
            return selectedElis !== null && selectedElis.mldOrg === null;
        }
        // Contact + provider names are no longer collected here — the BE
        // defaults the MLD-side provider row to a 'Default Provider'
        // placeholder and ships empty contact strings to ELIS (verified
        // to accept).  Admin can rename the provider after creation.
        const nameFilled = name.trim().length > 0;
        if (mode === 'blank') return nameFilled;
        // mode === 'ohd'
        return nameFilled && selectedOhd !== null;
    })();

    async function handleSubmit() {
        if (!canSubmit) return;
        setSaving(true);
        setError(null);
        try {
            if (mode === 'elis' && selectedElis) {
                const result = await AdminService.importOrganizationFromElis({
                    elisAccountId: selectedElis.elisAccountId,
                    subdomain: subdomain.trim().toLowerCase(),
                });
                await queryClient.invalidateQueries({queryKey: ['admin-organizations']});
                onClose();
                navigate(`/admin?orgId=${result.id}`);
                return;
            }

            const result = await AdminService.createOrganization({
                name: name.trim(),
                subdomain: subdomain.trim().toLowerCase(),
                ...(mode === 'ohd' && selectedOhd
                    ? {
                          ohd: {
                              organizationId: selectedOhd.organization_id,
                              organizationName: selectedOhd.organization_name,
                          },
                      }
                    : {}),
            });
            await queryClient.invalidateQueries({queryKey: ['admin-organizations']});
            onClose();
            navigate(`/admin?orgId=${result.id}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create organization');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={saving ? () => {} : onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-start justify-center px-4 py-12">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-2"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-2">
                            <Dialog.Panel className="w-full max-w-xl rounded-xl border border-mld-panel-border bg-white shadow-xl">
                                {/* Header */}
                                <div className="flex items-center justify-between border-b border-mld-panel-border px-5 py-3">
                                    <div className="text-sm font-semibold text-mld-menu-text">Add Organization</div>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={saving}
                                        className="rounded p-1 text-mld-muted-text hover:bg-mld-menu-background hover:text-mld-text-secondary">
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>

                                {/* Mode toggle */}
                                <div className="flex border-b border-mld-panel-border">
                                    {(['blank', 'ohd', 'elis'] as Mode[]).map((m) => (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => {
                                                setMode(m);
                                                setSelectedOhd(null);
                                                setSelectedElis(null);
                                                if (m !== 'elis') {
                                                    setElisSearch('');
                                                    setElisResults([]);
                                                }
                                                if (m === 'blank' || m === 'elis') setName('');
                                            }}
                                            className={clsx(
                                                'flex-1 py-2.5 text-center text-xs font-semibold transition',
                                                mode === m
                                                    ? 'border-b-2 border-mld-primary text-mld-primary'
                                                    : 'text-mld-muted-text hover:text-mld-menu-text'
                                            )}>
                                            {m === 'blank' ? 'Blank' : m === 'ohd' ? 'From OHD' : 'From ELIS'}
                                        </button>
                                    ))}
                                </div>

                                <div className="space-y-4 p-5">
                                    {/* OHD picker (From OHD mode) */}
                                    {mode === 'ohd' && !selectedOhd && (
                                        <div>
                                            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-mld-muted-text">
                                                Select OHD Organization
                                            </div>
                                            <div className="rounded-lg border border-mld-panel-border">
                                                <div className="flex items-center gap-2 border-b border-mld-panel-border px-3 py-2">
                                                    <Search className="h-3.5 w-3.5 text-mld-muted-text" />
                                                    <input
                                                        autoFocus
                                                        value={ohdSearch}
                                                        onChange={(e) => setOhdSearch(e.target.value)}
                                                        placeholder="Search OHD organizations..."
                                                        className="flex-1 border-none bg-transparent text-xs text-mld-menu-text focus:outline-none"
                                                    />
                                                </div>
                                                <div className="max-h-[200px] overflow-y-auto">
                                                    {ohdOrgsQuery.isLoading ? (
                                                        <div className="flex items-center gap-2 px-3 py-6 text-xs text-mld-muted-text">
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                            Loading…
                                                        </div>
                                                    ) : filteredOhd.length === 0 ? (
                                                        <div className="px-3 py-6 text-xs text-mld-muted-text">No results.</div>
                                                    ) : (
                                                        filteredOhd.map((o) => {
                                                            const linked = ohdToMld.get(o.organization_id);
                                                            return (
                                                                <button
                                                                    key={o.organization_id}
                                                                    type="button"
                                                                    onClick={() => selectOhdOrg(o)}
                                                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-[var(--mld-background)]">
                                                                    <div className="min-w-0 flex-1">
                                                                        <span className="font-semibold text-mld-menu-text">
                                                                            {o.organization_name}
                                                                        </span>
                                                                        <span className="ml-1.5 text-mld-muted-text">
                                                                            #{o.organization_id}
                                                                        </span>
                                                                    </div>
                                                                    {linked && (
                                                                        <span className="rounded-full bg-mld-warning/10 px-2 py-0.5 text-[10px] font-semibold text-mld-warning">
                                                                            Linked to {linked.name}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Selected OHD org chip */}
                                    {mode === 'ohd' && selectedOhd && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] font-semibold uppercase tracking-wide text-mld-muted-text">
                                                OHD Source:
                                            </span>
                                            <span className="rounded-full bg-mld-success/10 px-2.5 py-0.5 text-[11px] font-semibold text-mld-success">
                                                {selectedOhd.organization_name} #{selectedOhd.organization_id}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedOhd(null);
                                                    setName('');
                                                }}
                                                className="text-mld-muted-text hover:text-mld-text-secondary">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}

                                    {/* ELIS picker (From ELIS mode) */}
                                    {mode === 'elis' && !selectedElis && (
                                        <div>
                                            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-mld-muted-text">
                                                Select ELIS Account
                                            </div>
                                            <div className="rounded-lg border border-mld-panel-border">
                                                <div className="flex items-center gap-2 border-b border-mld-panel-border px-3 py-2">
                                                    <Search className="h-3.5 w-3.5 text-mld-muted-text" />
                                                    <input
                                                        autoFocus
                                                        value={elisSearch}
                                                        onChange={(e) => setElisSearch(e.target.value)}
                                                        placeholder="Search ELIS by name or ID (min 2 chars)…"
                                                        className="flex-1 border-none bg-transparent text-xs text-mld-menu-text focus:outline-none"
                                                    />
                                                </div>
                                                <div className="max-h-[200px] overflow-y-auto">
                                                    {debouncedElisSearch.length < 2 ? (
                                                        <div className="px-3 py-6 text-xs text-mld-muted-text">
                                                            Type at least 2 characters to search.
                                                        </div>
                                                    ) : elisSearching ? (
                                                        <div className="flex items-center gap-2 px-3 py-6 text-xs text-mld-muted-text">
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                            Searching ELIS…
                                                        </div>
                                                    ) : elisResults.length === 0 ? (
                                                        <div className="px-3 py-6 text-xs text-mld-muted-text">No results.</div>
                                                    ) : (
                                                        elisResults.map((r) => {
                                                            const disabled = r.mldOrg !== null;
                                                            return (
                                                                <button
                                                                    key={r.elisAccountId}
                                                                    type="button"
                                                                    disabled={disabled}
                                                                    onClick={() => {
                                                                        if (disabled) return;
                                                                        setSelectedElis(r);
                                                                        setName(r.name);
                                                                    }}
                                                                    className={clsx(
                                                                        'flex w-full items-center gap-2 px-3 py-2 text-left text-xs',
                                                                        disabled
                                                                            ? 'cursor-not-allowed opacity-60'
                                                                            : 'hover:bg-[var(--mld-background)]'
                                                                    )}>
                                                                    <div className="min-w-0 flex-1">
                                                                        <span className="font-semibold text-mld-menu-text">
                                                                            {r.name}
                                                                        </span>
                                                                        <span className="ml-1.5 text-mld-muted-text">
                                                                            #{r.elisAccountId}
                                                                        </span>
                                                                    </div>
                                                                    {r.mldOrg && (
                                                                        <span className="rounded-full bg-mld-warning/10 px-2 py-0.5 text-[10px] font-semibold text-mld-warning">
                                                                            Already imported as {r.mldOrg.subdomain}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Selected ELIS account chip */}
                                    {mode === 'elis' && selectedElis && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] font-semibold uppercase tracking-wide text-mld-muted-text">
                                                ELIS Source:
                                            </span>
                                            <span className="rounded-full bg-mld-success/10 px-2.5 py-0.5 text-[11px] font-semibold text-mld-success">
                                                {selectedElis.name} #{selectedElis.elisAccountId}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedElis(null);
                                                    setName('');
                                                }}
                                                className="text-mld-muted-text hover:text-mld-text-secondary">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}

                                    {/* ELIS-only: subdomain is the only thing the admin supplies — name + providers come from ELIS */}
                                    {mode === 'elis' && selectedElis && (
                                        <Field label="Subdomain" required>
                                            <input
                                                type="text"
                                                value={subdomain}
                                                onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                                className={clsx(
                                                    'w-full rounded-md border bg-white px-3 py-2 text-xs text-mld-menu-text focus:outline-none',
                                                    existingSubdomain
                                                        ? 'border-mld-danger/30 focus:border-mld-danger'
                                                        : 'border-mld-panel-border focus:border-mld-primary'
                                                )}
                                                placeholder="e.g. hp"
                                                autoFocus
                                            />
                                            {existingSubdomain && (
                                                <div className="mt-1 text-[11px] text-mld-danger">
                                                    This subdomain is already in use.
                                                </div>
                                            )}
                                        </Field>
                                    )}

                                    {/* Form fields — shown always in Blank mode, or after OHD pick */}
                                    {(mode === 'blank' || (mode === 'ohd' && selectedOhd)) && (
                                        <>
                                            <Field label="Organization Name" required>
                                                <input
                                                    type="text"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs text-mld-menu-text focus:border-mld-primary focus:outline-none"
                                                    placeholder="e.g. HP"
                                                    autoFocus={mode === 'blank'}
                                                />
                                            </Field>

                                            <Field label="Subdomain" required>
                                                <input
                                                    type="text"
                                                    value={subdomain}
                                                    onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                                    className={clsx(
                                                        'w-full rounded-md border bg-white px-3 py-2 text-xs text-mld-menu-text focus:outline-none',
                                                        existingSubdomain
                                                            ? 'border-mld-danger/30 focus:border-mld-danger'
                                                            : 'border-mld-panel-border focus:border-mld-primary'
                                                    )}
                                                    placeholder="e.g. hp"
                                                />
                                                {existingSubdomain && (
                                                    <div className="mt-1 text-[11px] text-mld-danger">
                                                        This subdomain is already in use.
                                                    </div>
                                                )}
                                            </Field>

                                        </>
                                    )}
                                </div>

                                {/* Error */}
                                {error && (
                                    <div className="border-t border-mld-danger/20 bg-mld-danger/5 px-5 py-2 text-[12px] text-mld-danger">
                                        {error}
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="flex justify-end gap-2 border-t border-mld-panel-border px-5 py-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={saving}
                                        className="rounded-md px-3 py-1.5 text-xs font-semibold text-mld-text-secondary hover:bg-mld-menu-background">
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={!canSubmit}
                                        className={clsx(
                                            'inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-semibold text-white transition',
                                            canSubmit
                                                ? 'bg-mld-menu-text hover:bg-[#2d3a4a]'
                                                : 'cursor-not-allowed bg-mld-muted-text/40'
                                        )}>
                                        {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                                        {mode === 'elis' ? 'Import Organization' : 'Create Organization'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

const Field: React.FC<{label: string; required?: boolean; children: React.ReactNode}> = ({
    label,
    required,
    children,
}) => (
    <div>
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-mld-muted-text">
            {label}
            {required && <span className="ml-0.5 text-mld-danger/60">*</span>}
        </div>
        {children}
    </div>
);

export default AddOrganizationModal;
